const DB_NAME = 'token-lean-ide';
const DB_VERSION = 3;

export const STORES = {
  settings: 'settings', // key -> value (optimization settings, api key, templates, path aliases)
  files: 'files', // keyPath: path -> ProjectFileRef + optional FileSystemFileHandle
  conversations: 'conversations', // keyPath: id -> ConversationRecord
  embeddings: 'embeddings', // keyPath: contentHash -> cached embedding vector (A3, populated once implemented)
  benchmarkContext: 'benchmarkContext', // keyPath: hash -> { hash, content }, content-addressed so repeated context across benchmark runs is stored once
  compressorCache: 'compressorCache', // keyPath: hash -> memoized B4 semantic-compression result, keyed by text+rate+model version
} as const;

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const opening: Promise<IDBDatabase> = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORES.settings)) {
        db.createObjectStore(STORES.settings);
      }
      if (!db.objectStoreNames.contains(STORES.files)) {
        db.createObjectStore(STORES.files, { keyPath: 'path' });
      }
      if (!db.objectStoreNames.contains(STORES.conversations)) {
        db.createObjectStore(STORES.conversations, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.embeddings)) {
        db.createObjectStore(STORES.embeddings, { keyPath: 'contentHash' });
      }
      if (!db.objectStoreNames.contains(STORES.benchmarkContext)) {
        db.createObjectStore(STORES.benchmarkContext, { keyPath: 'hash' });
      }
      if (!db.objectStoreNames.contains(STORES.compressorCache)) {
        db.createObjectStore(STORES.compressorCache, { keyPath: 'hash' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // A tab running a newer DB_VERSION cannot upgrade while this connection is open. Closing on
      // demand is what keeps the *next* version bump from hanging whichever tab was left open.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    // Another tab is still holding the previous DB_VERSION open, so our upgrade can't start.
    // Reject rather than sit here silently: without this, every db call in this tab waits forever.
    req.onblocked = () =>
      reject(new Error('IndexedDB upgrade blocked by another open tab — close other tabs of this app and reload.'));
  }).catch((err) => {
    // Don't poison the singleton: a blocked upgrade succeeds once the other tab goes away, so let the
    // next caller retry instead of failing for the rest of the page's life.
    if (dbPromise === opening) dbPromise = null;
    throw err;
  });
  dbPromise = opening;
  return opening;
}

export async function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function idbPut(store: string, value: unknown, key?: IDBValidKey): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    key === undefined ? tx.objectStore(store).put(value) : tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbDelete(store: string, key: IDBValidKey): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbCount(store: string): Promise<number> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Bulk delete in a single transaction — looping idbDelete() costs one transaction per key, which is
 * what makes cache pruning expensive enough to notice. */
export async function idbDeleteMany(store: string, keys: IDBValidKey[]): Promise<void> {
  if (keys.length === 0) return;
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const objectStore = tx.objectStore(store);
    for (const key of keys) objectStore.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGetAll<T>(store: string): Promise<T[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

export async function idbClear(store: string): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
